import { map } from 'lodash';
import { diff } from 'components/transition/private/util/diff';
import { pack } from 'components/transition/private/util/pack';
import { useEffect } from 'libs/react';
import { useState } from 'libs/react';
import { useValue } from 'libs/react';
import { Animator } from 'components/transition/private/Animator';
import { SequenceAnimator } from 'components/transition/private/SequenceAnimator';
import { TogetherAnimator } from 'components/transition/private/TogetherAnimator';
import { React } from 'libs/react';
import './Transition.scss';
/**
 * @component Transition
 * @since 1.0.0
 */
export function Transition(props) {
    const { appear, serial, presentClassName, dismissClassName, presentDuration, dismissDuration, presentDelay, dismissDelay, presentBeginHandler, dismissBeginHandler, presentFinishHandler, dismissFinishHandler, onBeforePresent, onBeforeDismiss, onAfterPresent, onAfterDismiss, children } = Object.assign({ appear: true, serial: true, presentClassName: 'present', dismissClassName: 'dismiss', presentDuration: 500, dismissDuration: 500, presentDelay: 0, dismissDelay: 0 }, props);
    const options = {
        presentClassName,
        dismissClassName,
        presentDuration,
        dismissDuration,
        presentDelay,
        dismissDelay,
        presentBeginHandler,
        dismissBeginHandler,
        presentFinishHandler,
        dismissFinishHandler,
        onBeforePresent,
        onBeforeDismiss,
        onAfterPresent,
        onAfterDismiss
    };
    const self = useValue(() => ({
        appear: true,
        serial: true,
        started: false,
        outcome: {},
        animator: new Animator(options),
        insertContent,
        removeContent,
        callback: null,
        get presentCount() {
            return Object.keys(self.animator.present).length;
        },
        get dismissCount() {
            return Object.keys(self.animator.dismiss).length;
        }
    }));
    self.appear = appear;
    self.serial = serial;
    self.animator.manage(self);
    const [content, setContent] = useState({});
    /**
     * @function insertDismissedContent
     * @since 1.0.0
     * @hidden
     */
    function insertContent(key, value) {
        setContent((content) => insert(content, key, value));
    }
    /**
     * @function removeContent
     * @since 1.0.0
     * @hidden
     */
    function removeContent(key) {
        setContent((content) => remove(content, key));
    }
    /**
     * @function render
     * @since 1.0.0
     * @hidden
     */
    function render() {
        const nodes = map(content, (child) => React.cloneElement(child, {
            ref: (ref) => {
                self.animator.prepare(child.key, ref);
            }
        }));
        return nodes;
    }
    useEffect(() => {
        const current = self.animator.content;
        const outcome = filter(pack(children));
        const empty = Object.keys(current).length == 0;
        const [insert, remove, update] = diff(current, outcome);
        const next = diff(self.outcome, outcome);
        self.outcome = outcome;
        setContent(Object.assign(Object.assign({}, content), update));
        const updateKeys = Object.keys(update);
        const insertKeys = Object.keys(insert);
        const removeKeys = Object.keys(remove);
        if (insertKeys.length == 0 && removeKeys.length == 0) {
            if (updateKeys.length) {
                const [insert, remove] = next;
                let changes = false;
                for (const key in update) {
                    if (key in insert || key in remove) {
                        changes = true;
                    }
                }
                if (changes == false) {
                    return;
                }
            }
            else {
                return;
            }
        }
        self.animator.abort();
        let animator;
        if (self.appear == false && empty) {
            animator = new Animator(options);
        }
        else {
            if (self.serial) {
                animator = new SequenceAnimator(options);
            }
            else {
                animator = new TogetherAnimator(options);
            }
        }
        animator.manage(self);
        for (const key in insert)
            animator.insert(key, insert[key], self.animator);
        for (const key in remove)
            animator.remove(key, remove[key], self.animator);
        for (const key in update) {
            if (key in next[0])
                animator.insert(key, update[key], self.animator);
            if (key in next[1])
                animator.remove(key, update[key], self.animator);
            if (key in next[2])
                animator.update(key, update[key], self.animator);
        }
        self.animator = animator;
        self.animator.start();
    }, [children]);
    /**
     * Invokes the render callback.
     * @since 1.0.0
     */
    useEffect(() => {
        if (self.callback) {
            self.callback();
            self.callback = null;
        }
    });
    return render();
}
/**
 * @component TransitionItem
 * @since 1.0.0
 */
export class TransitionItem extends React.Component {
    /**
     * @inherited
     * @method render
     * @since 1.0.0
     */
    render() {
        return this.props.children;
    }
}
/**
 * @method insert
 * @since 1.0.0
 * @hidden
 */
function insert(object, key, value) {
    object[key] = value;
    return Object.assign({}, object);
}
/**
 * @method remove
 * @since 1.0.0
 * @hidden
 */
function remove(object, key) {
    delete object[key];
    return Object.assign({}, object);
}
/**
 * @method filter
 * @since 1.0.0
 * @hidden
 */
function filter(content) {
    const output = {};
    for (const key in content) {
        const child = content[key];
        if (child == null) {
            continue;
        }
        const props = child.props;
        if (props) {
            if ('when' in props) {
                const condition = Boolean(props.when);
                if (condition == false) {
                    continue;
                }
            }
        }
        output[key] = child;
    }
    return output;
}
